import React, { useState } from 'react';
import { useTranslation } from '../../i18n/useTranslation';
import PhoneInput from '../PhoneInput/PhoneInput';
import api from '../../api';
import './ContactModal.css';

const ContactModal = ({ isOpen, onClose }) => {
  const { t, phone } = useTranslation();
  const [step, setStep] = useState('choose');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    message: '',
    preferredContact: 'phone',
    telegramUsername: ''
  });

  if (!isOpen) return null;

  const isTelegram = formData.preferredContact === 'telegram';

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleContactTypeChange = (e) => {
    setFormData({
      ...formData,
      preferredContact: e.target.value,
      // Очистка полей при переключении
      phone: e.target.value === 'telegram' ? '' : formData.phone,
      telegramUsername: e.target.value === 'phone' ? '' : formData.telegramUsername
    });
    setError('');
  };

  const handleUsernameChange = (e) => {
    // Убираем @, пробелы и спецсимволы — только буквы, цифры, подчёркивания
    const val = e.target.value
      .replace(/@/g, '')
      .replace(/\s/g, '')
      .replace(/[^a-zA-Z0-9_]/g, '');
    setFormData({ ...formData, telegramUsername: val });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Имя обязательно всегда
    if (!formData.name.trim()) {
      setError(t('contact.errorRequired'));
      return;
    }

    if (isTelegram) {
      // Telegram — username обязателен
      if (!formData.telegramUsername.trim()) {
        setError(t('contact.errorTgUsername'));
        return;
      }
      if (formData.telegramUsername.trim().length < 3) {
        setError(t('contact.errorTgUsernameShort'));
        return;
      }
    } else {
      // Телефон — обязателен и полный
      if (!formData.phone) {
        setError(t('contact.errorRequired'));
        return;
      }
      const digits = formData.phone.replace(/\D/g, '');
      if (digits.length < 11) {
        setError(t('contact.errorPhone'));
        return;
      }
    }

    setLoading(true);
    setError('');

    try {
      await api.post('/api/contact/telegram', {
        name: formData.name,
        phone: isTelegram ? '' : formData.phone,
        message: formData.message,
        preferredContact: formData.preferredContact,
        telegramUsername: isTelegram ? formData.telegramUsername : ''
      });
      setStep('success');
      setFormData({
        name: '', phone: '', message: '',
        preferredContact: 'phone', telegramUsername: ''
      });
    } catch (err) {
      setError(t('contact.errorSend'));
    }
    setLoading(false);
  };

  const handleClose = () => {
    setStep('choose');
    setError('');
    onClose();
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) handleClose();
  };

  return (
    <div className="modal-overlay" onClick={handleOverlayClick}>
      <div className="modal">
        <button className="modal__close" onClick={handleClose}>✕</button>

        {/* ===== ШАГ 1: ВЫБОР ===== */}
        {step === 'choose' && (
          <div className="modal__choose">
            <div className="modal__icon-wrapper">
              <span className="modal__main-icon">📞</span>
            </div>
            <h2 className="modal__title">{t('contact.title')}</h2>
            <p className="modal__subtitle">{t('contact.subtitle')}</p>

            <div className="modal__options">
              <a href={`tel:${phone.replace(/\s/g, '')}`} className="modal__option modal__option--call">
                <div className="modal__option-icon">📱</div>
                <div className="modal__option-content">
                  <h3>{t('contact.callTitle')}</h3>
                  <p>{t('contact.callDesc')}</p>
                  <span className="modal__option-phone">{phone}</span>
                </div>
                <span className="modal__option-arrow">→</span>
              </a>

              <button
                className="modal__option modal__option--form"
                onClick={() => setStep('form')}
              >
                <div className="modal__option-icon">✉️</div>
                <div className="modal__option-content">
                  <h3>{t('contact.formTitle')}</h3>
                  <p>{t('contact.formDesc')}</p>
                  <span className="modal__option-tag">{t('contact.formTag')}</span>
                </div>
                <span className="modal__option-arrow">→</span>
              </button>
            </div>

            <div className="modal__messengers">
              <p>{t('contact.orWrite')}</p>
              <div className="modal__messenger-links">
                <a
                  href="https://t.me/PowerTemur"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="modal__messenger modal__messenger--tg"
                >
                  ✈️ Telegram
                </a>
              </div>
            </div>
          </div>
        )}

        {/* ===== ШАГ 2: ФОРМА ===== */}
        {step === 'form' && (
          <div className="modal__form-step">
            <button className="modal__back" onClick={() => { setStep('choose'); setError(''); }}>
              ← {t('contact.back')}
            </button>

            <h2 className="modal__title">{t('contact.leaveContacts')}</h2>
            <p className="modal__subtitle">{t('contact.leaveContactsDesc')}</p>

            <form onSubmit={handleSubmit} className="modal__form">
              {/* Имя */}
              <div className="modal__field">
                <label>{t('contact.nameLabel')} *</label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder={t('contact.namePlaceholder')}
                  required
                />
              </div>

              {/* Способ связи */}
              <div className="modal__field">
                <label>{t('contact.preferredContact')}</label>
                <div className="modal__radio-group">
                  <label className="modal__radio">
                    <input
                      type="radio"
                      name="preferredContact"
                      value="phone"
                      checked={!isTelegram}
                      onChange={handleContactTypeChange}
                    />
                    <span className="modal__radio-mark"></span>
                    <span>📱 {t('contact.byPhone')}</span>
                  </label>

                  <label className="modal__radio">
                    <input
                      type="radio"
                      name="preferredContact"
                      value="telegram"
                      checked={isTelegram}
                      onChange={handleContactTypeChange}
                    />
                    <span className="modal__radio-mark"></span>
                    <span>✈️ Telegram</span>
                  </label>
                </div>
              </div>

              {/* ===== ПОЛЕ ЗАВИСИТ ОТ ВЫБОРА ===== */}
              {isTelegram ? (
                /* Telegram username */
                <div className="modal__field modal__field--animated" key="tg-field">
                  <label>{t('contact.tgUsernameLabel')} *</label>
                  <div className="modal__tg-input-wrapper">
                    <input
                      type="text"
                      value={formData.telegramUsername}
                      onChange={handleUsernameChange}
                      placeholder={t('contact.tgUsernamePlaceholder')}
                      className="modal__tg-input"
                      autoComplete="off"
                      autoFocus
                    />
                    {formData.telegramUsername.length >= 3 && (
                      <span className="modal__tg-check">✓</span>
                    )}
                  </div>
                  <p className="modal__field-hint">{t('contact.tgUsernameHint')}</p>
                </div>
              ) : (
                /* Телефон */
                <div className="modal__field modal__field--animated" key="phone-field">
                  <label>{t('contact.phoneLabel')} *</label>
                  <PhoneInput
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                  />
                </div>
              )}

              {/* Сообщение */}
              <div className="modal__field">
                <label>{t('contact.messageLabel')}</label>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  placeholder={t('contact.messagePlaceholder')}
                  rows={3}
                />
              </div>

              {error && (
                <div className="modal__error">❌ {error}</div>
              )}

              <button
                type="submit"
                className="btn btn-primary btn-lg"
                style={{ width: '100%' }}
                disabled={loading}
              >
                {loading ? t('contact.sending') : t('contact.sendButton')}
              </button>

              <p className="modal__privacy">{t('contact.privacy')}</p>
            </form>
          </div>
        )}

        {/* ===== ШАГ 3: УСПЕХ ===== */}
        {step === 'success' && (
          <div className="modal__success">
            <div className="modal__success-icon">✅</div>
            <h2 className="modal__title">{t('contact.successTitle')}</h2>
            <p className="modal__subtitle">{t('contact.successDesc')}</p>
            <button className="btn btn-primary" onClick={handleClose}>
              {t('contact.close')}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ContactModal;